import { DefaultLoader } from '@euler/app/components/loaders/DefaultLoader';
import { Aside } from '@euler/components/Aside';
import { ModalWrapper } from '@euler/components/ModalWrapper';
import { adaptiveWrapperOptions } from '@euler/functions/navigationOptionsBuilder';
import { useResponsiveContext } from '@euler/functions/responsive/ResponsiveContext';
import { useIsMobileLayout } from '@euler/functions/responsiveness';
import { useSystemMetrics } from '@euler/functions/useSystemMetrics';
import { WrappedNavigatorScreen } from '@euler/functions/wrapNavigatorScreen';
import { BottomSheetModalProvider } from '@gorhom/bottom-sheet';
import { useRoute } from '@react-navigation/native';
import {
  StackNavigationOptions,
  StackNavigationProp,
} from '@react-navigation/stack';
import { FC, ReactNode, Suspense, memo } from 'react';
import { ScrollView } from 'react-native';

export function wrapTaskNavigatorScreen<
  T extends object,
  U = StackNavigationOptions,
>(
  Component: FC<T>,
  options?:
    | U
    | ((props: {
        route: { params: T };
        navigation: StackNavigationProp<any>;
      }) => U),
  params?: {
    aside?: boolean;
    dismissible?: boolean;
    extraProps?: any;
    fallback?: NonNullable<ReactNode> | null;
  },
): WrappedNavigatorScreen<T> {
  const NavigatorScreenWrapper = memo(() => {
    const isMobile = useIsMobileLayout();

    if (isMobile) {
      return <Content Component={Component} fallback={params?.fallback} />;
    }
    if (params?.aside) {
      return (
        <Aside dismissible={params?.dismissible ?? true}>
          <Content Component={Component} fallback={params?.fallback} />
        </Aside>
      );
    }

    return (
      <ModalWrapper dismissible={params?.dismissible ?? true}>
        <Content Component={Component} fallback={params?.fallback} />
      </ModalWrapper>
    );
  }) as any;

  NavigatorScreenWrapper.component = Component;
  NavigatorScreenWrapper.options = adaptiveWrapperOptions(options as any);
  NavigatorScreenWrapper.extraProps = params?.extraProps;

  return NavigatorScreenWrapper;
}

const Content = memo(
  ({
    Component,
    fallback,
  }: {
    Component: FC<any>;
    fallback?: NonNullable<ReactNode> | null;
  }) => {
    const { rpx } = useResponsiveContext();
    const { navBarHeight } = useSystemMetrics();
    const maybeParams = useRoute().params as any;
    const params =
      maybeParams != null &&
      'path' in maybeParams &&
      'screen' in maybeParams &&
      'params' in maybeParams
        ? (maybeParams.params as any)
        : (maybeParams as any);
    return (
      <Suspense
        fallback={
          fallback === undefined ? (
            <ScrollView
              css={`
                flex: 1;
              `}
              contentContainerStyle={[
                {
                  flexGrow: 1,
                  paddingTop: navBarHeight + rpx(15),
                  justifyContent: 'flex-start',
                  alignItems: 'center',
                },
              ]}
            >
              <DefaultLoader />
            </ScrollView>
          ) : (
            fallback
          )
        }
      >
        <BottomSheetModalProvider>
          <Component {...params} />
        </BottomSheetModalProvider>
      </Suspense>
    );
  },
);
